import React, { useState, useEffect, useCallback } from 'react';
import { getHostels, addHostel, updateHostel, deleteHostel } from '../api';
import { FiHome, FiX } from 'react-icons/fi';
import { usePermissions } from '../utils/permissions';

const emptyForm = {
  name: '', incharge_name: '', phone: '', email: '',
  password: '', address: '', total_rooms: '', notes: '', is_active: 1,
  perm_edit_approval: 1, perm_delete_approval: 1, perm_checkout_approval: 1,
  admissions_edit_hidden: 0,
  attendance_edit_hidden: 0, attendance_edit_approval: 1,
  meals_edit_hidden: 0, meals_edit_approval: 1,
};

export default function Hostels() {
  const { can } = usePermissions();
  const [hostels, setHostels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getHostels();
      setHostels(res.data);
    } catch (e) {
      setError('Failed to load hostels');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setError(''); setShowModal(true); };
  const openEdit = (h) => {
    setEditing(h);
    setForm({ ...h, password: '' });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.incharge_name || !form.email) {
      setError('Hostel name, incharge name and email are required');
      return;
    }
    if (!/^[a-zA-Z\s'.-]{2,}$/.test(form.incharge_name.trim())) {
      setError('In-charge name must contain letters only');
      return;
    }
    if (form.phone && !/^03\d{9}$/.test(form.phone.replace(/[\s-]/g, ''))) {
      setError('Phone must be Pakistani format: 03XXXXXXXXX (11 digits)');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Invalid email address');
      return;
    }
    if (!editing && !form.password) {
      setError('Password is required when creating a hostel');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await updateHostel(editing.id, form);
      } else {
        await addHostel(form);
      }
      setShowModal(false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save hostel');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await deleteHostel(id);
      setDeleteConfirm(null);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to delete hostel');
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title"><FiHome /> Hostel Management</h2>
          <p style={{ color: '#6b7280', margin: '4px 0 0' }}>Manage hostels and their portal credentials</p>
        </div>
        {can('hostels', 'create') && <button className="btn btn-primary" onClick={openAdd}>+ Add Hostel</button>}
      </div>
      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner"></div></div>
      ) : (
        <div className="table-container" style={{ marginTop: '20px' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Hostel Name</th>
                <th>In-charge</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Capacity</th>
                <th>Occupied</th>
                <th>Vacant</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {hostels.length === 0 ? (
                <tr><td colSpan="10" style={{ textAlign: 'center', padding: '30px', color: '#9ca3af' }}>No hostels found. Add your first hostel.</td></tr>
              ) : hostels.map((h, i) => {
                const capacity = h.total_rooms || 0;
                const occupied = h.active_residents || 0;
                const vacant = capacity ? Math.max(0, capacity - occupied) : null;
                return (
                <tr key={h.id}>
                  <td>{i + 1}</td>
                  <td><strong style={{ color: '#0e1e45' }}>{h.name}</strong></td>
                  <td>{h.incharge_name}</td>
                  <td>{h.phone || '—'}</td>
                  <td>{h.email}</td>
                  <td>{capacity || <span style={{ color: '#9ca3af' }} title="Set Total Rooms to enable capacity/vacancy tracking">Not set</span>}</td>
                  <td>{occupied}</td>
                  <td>
                    {vacant === null ? '—' : (
                      <span style={{ fontWeight: 600, color: vacant === 0 ? '#ef4444' : '#10b981' }}>{vacant}{vacant === 0 && ' (Full)'}</span>
                    )}
                  </td>
                  <td>
                    <span style={{
                      padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                      background: h.is_active ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)',
                      color: h.is_active ? '#10b981' : '#ef4444'
                    }}>
                      {h.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {can('hostels', 'edit') && <button className="btn btn-secondary btn-small" onClick={() => openEdit(h)}>Edit</button>}
                      {can('hostels', 'delete') && <button className="btn btn-danger btn-small" onClick={() => setDeleteConfirm(h.id)}>Delete</button>}
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px', width: '95%' }}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Hostel' : 'Add New Hostel'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><FiX /></button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: '14px' }}>
              {error && <div className="alert alert-error">{error}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div className="form-group">
                  <label>Hostel Name *</label>
                  <input className="form-control" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Boys Hostel A" />
                </div>
                <div className="form-group">
                  <label>In-charge Name *</label>
                  <input className="form-control" value={form.incharge_name} onChange={e => setForm(p => ({ ...p, incharge_name: e.target.value }))} placeholder="Enter in-charge full name" />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div className="form-group">
                  <label>Email *</label>
                  <input className="form-control" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Enter hostel login email address" />
                </div>
                <div className="form-group">
                  <label>{editing ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                  <input className="form-control" type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder={editing ? '••••••••' : 'Set password'} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div className="form-group">
                  <label>Phone</label>
                  <input className="form-control" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="03001234567 (11 digits)" />
                </div>
                <div className="form-group">
                  <label>Total Rooms (Capacity)</label>
                  <input className="form-control" type="number" value={form.total_rooms} onChange={e => setForm(p => ({ ...p, total_rooms: e.target.value }))} placeholder="Enter total number of rooms" />
                  <small style={{ color: 'var(--text-muted)' }}>This is the hostel's max resident capacity — vacancy is calculated automatically as Capacity − Active Residents. Leave 0 for no limit.</small>
                </div>
              </div>

              <div className="form-group">
                <label>Address</label>
                <input className="form-control" value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} placeholder="Enter hostel full address" />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea className="form-control" rows="2" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes" />
              </div>

              {editing && (
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-control" value={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: +e.target.value }))}>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              )}

              <div className="form-group">
                <label style={{ fontWeight: '600', color: '#374151', marginBottom: '10px', display: 'block' }}>Resident Record Permissions — Hostel must request admin approval for:</label>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {[
                    ['perm_edit_approval', 'Edit resident records'],
                    ['perm_delete_approval', 'Delete resident records'],
                    ['perm_checkout_approval', 'Checkout (exit) residents'],
                  ].map(([key, label]) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '7px 10px', background: form[key] ? 'rgba(245,158,11,0.08)' : 'rgba(16,185,129,0.06)', borderRadius: '6px', border: `1px solid ${form[key] ? 'rgba(245,158,11,0.25)' : 'rgba(16,185,129,0.2)'}` }}>
                      <input type="checkbox" checked={!!form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.checked ? 1 : 0 }))} style={{ width: '16px', height: '16px', accentColor: '#0e1e45' }} />
                      <span style={{ fontSize: '13px', color: '#374151' }}>{label}</span>
                      <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: '600', color: form[key] ? '#d97706' : '#0e1e45' }}>{form[key] ? 'Needs Approval' : 'Direct Action'}</span>
                    </label>
                  ))}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '7px 10px', background: form.admissions_edit_hidden ? 'rgba(239,68,68,0.08)' : 'transparent', borderRadius: '6px', border: `1px solid ${form.admissions_edit_hidden ? 'rgba(239,68,68,0.25)' : '#e5e7eb'}` }}>
                    <input type="checkbox" checked={!!form.admissions_edit_hidden} onChange={e => setForm(p => ({ ...p, admissions_edit_hidden: e.target.checked ? 1 : 0 }))} style={{ width: '16px', height: '16px', accentColor: '#ef4444' }} />
                    <span style={{ fontSize: '13px', color: '#374151' }}>Hide editing entirely (overrides the approval setting above — hostel cannot edit or request edits)</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label style={{ fontWeight: '600', color: '#374151', marginBottom: '10px', display: 'block' }}>Attendance &amp; Meals — Editing an already-recorded day</label>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 10px' }}>Marking a fresh, unmarked day is always direct. This only governs correcting a day already recorded.</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  {[
                    ['attendance', 'Attendance'],
                    ['meals', 'Meals'],
                  ].map(([feature, label]) => {
                    const hiddenKey = `${feature}_edit_hidden`;
                    const approvalKey = `${feature}_edit_approval`;
                    const mode = form[hiddenKey] ? 'hidden' : (form[approvalKey] ? 'request' : 'direct');
                    return (
                      <div key={feature} className="form-group" style={{ marginBottom: 0 }}>
                        <label>{label}</label>
                        <select
                          className="form-control"
                          value={mode}
                          onChange={e => {
                            const v = e.target.value;
                            setForm(p => ({
                              ...p,
                              [hiddenKey]: v === 'hidden' ? 1 : 0,
                              [approvalKey]: v === 'request' ? 1 : 0,
                            }));
                          }}
                        >
                          <option value="direct">Direct — hostel can correct freely</option>
                          <option value="request">Request — correction needs admin approval</option>
                          <option value="hidden">Hidden — hostel cannot correct at all</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : (editing ? 'Update Hostel' : 'Add Hostel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header"><h3>Confirm Delete</h3></div>
            <div className="modal-body">
              <p style={{ color: '#d1fae5' }}>Are you sure you want to delete this hostel? All associated admission records will also be deleted.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
